import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import dayjs from 'dayjs';
import { useState } from 'react';
import CrossFilledIcon from '../../../../../../assets/Svg/crossFilledIcon';

import { Input } from '../../../../../../common/_custom';
import { GREEN_BG_WHITE_TICK_CIRCLE, GREEN_CHECK_SMALL } from '../../../../../../assets/hostedassets';

import styles from '../DirectorDetails.module.scss';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import FetchFillIcon from '../../../../../../assets/Svg/FetchFilIcon';
import { ONE_DAY_IN_MILLISECONDS } from '../../../../../../utils/constants/commonConstants';
import { creditBureauConfig } from '../../../../../../store/investor/transformer';
import { getEffectiveDirectorFieldConfig } from '../addDirectorFieldRules';

interface ConsentStatus {
    [key: string]: {
        bureauName: string;
        lastFetchedAt: Date;
        isConsentGiven: boolean;
        isBureauError?: boolean;
    };
}
interface DirectorDetails {
    directorName: string;
    phoneNo: string;
    consentStatus?: ConsentStatus;
    lastInviteSentAt?: string;
    isNewDir?: boolean;
    updatedAt?: number;
    cibilConsentStatus?: ConsentStatus;
    pan?: string;
}

// `lastFetchedAt` arrives over the wire as an ISO string from aica's Spring Boot
// Jackson serializer, not as an epoch number. The original implementation only
// handled `number | Date` — `Date.now() - "<iso-string>"` coerces to NaN, which
// silently returns false and kept "Fetched" frozen for any director older than
// 24h. RECUR-25636 fix: also parse string input. Falsy / NaN both fall back to
// `true` (treat as stale) so a missing or malformed timestamp surfaces Refetch
// rather than locking the row.
const isTimeStampOneDayOld = (timestamp: number | Date | string | undefined) => {
    if (!timestamp) return true;
    let ms: number;
    if (timestamp instanceof Date) {
        ms = timestamp.getTime();
    } else if (typeof timestamp === 'string') {
        ms = new Date(timestamp).getTime();
    } else {
        ms = timestamp;
    }
    if (Number.isNaN(ms)) return true;
    return new Date().getTime() - ms > ONE_DAY_IN_MILLISECONDS;
};

interface DirectorInputFieldProps {
    director: DirectorDetails;
    index: number;
    handleChange: (e: string, index: number, name: string) => void;
    sendLink: (
        name: string,
        setDisableSendLink: (value: boolean) => void,
        input: number,
        source: string,
        flowType?: string,
        phoneNo?: string,
        pan?: string,
    ) => void;
    errorMessage: string;
    isLoading?: boolean;
    isError: boolean;
    fieldErrors?: Record<string, string>;
    isAdminSide?: boolean;
    placeHolder?: string;
    creditBureauConfig?: any;
    validateInput(value: string, name: string): boolean;
    onRemove?: () => void;
    onGiveConsent?: () => void;
    lastConsent?: { consenterName: string; consentedAt: string };
    isReceived?: boolean;
    // True when the current borrower-side session is actually an internal /
    // lender "View Account" (summon) session — Give Consent must render in a
    // greyed, non-clickable state so internal users can't capture consent on
    // behalf of the borrower. The borrower's own real session leaves this
    // false and the CTA stays active.
    disableGiveConsent?: boolean;
    // RECUR-27297 — per-PNO "consent mandatory" config. When true, the PNO
    // Fetch/Refetch action is gated on `lastConsent`: the BE is the single
    // authority and rejects an ungated fetch anyway, but this avoids shipping a
    // button that can only ever error. Only meaningful on the admin (PNO) side.
    consentRequired?: boolean;
    /**
     * Save a not-yet-persisted director without fetching bureau data (RECUR-27297). Present
     * only when the save-then-consent-then-fetch flow applies; when absent the row keeps the
     * pre-existing single-step Fetch behaviour.
     */
    saveDirector?: (index: number) => void;
}

function DirectorInputField({
    director,
    index,
    handleChange,
    sendLink,
    errorMessage,
    isLoading,
    isError,
    fieldErrors,
    isAdminSide,
    placeHolder,
    creditBureauConfig,
    validateInput,
    onRemove,
    onGiveConsent,
    lastConsent,
    isReceived,
    disableGiveConsent,
    consentRequired,
    saveDirector,
}: DirectorInputFieldProps) {
    const { FormContainer, Field, DisabledBannerText, BannerText, BannerTextVerified } = styles;

    const [disableSendLink, setDisableSendLink] = useState<boolean>(true);

    // Timestamp of the last successful credit bureau data fetch for this director.
    const lastFetchedAt = director?.consentStatus?.[creditBureauConfig?.name]?.lastFetchedAt;
    const reFetchSoftPull = isTimeStampOneDayOld(lastFetchedAt) && isAdminSide;

    // RECUR-27297 — a director that isn't on record yet can't be fetched (the fetch needs
    // consent, and consent is keyed by a directorId that only exists once saved). So the row's
    // first action is Save; it becomes Fetch on the next render, once the director is persisted.
    const isSaveStep = !!isAdminSide && !!consentRequired && !!director.isNewDir && !!saveDirector;

    // RECUR-27297 gate — fail open on purpose: this only renders the disabled style;
    // the BE gate (CibilServiceImpl.sendConsentLinkToDirector) is the single authority
    // and rejects an ungated fetch regardless of what the FE renders here.
    //
    // Never blocks the Save step: saving is what makes consent possible in the first place,
    // so gating it on consent would deadlock the row.
    const isFetchBlockedByConsent = !!isAdminSide && !!consentRequired && !lastConsent && !isSaveStep;

    const handleSaveClick = () => {
        // Only the name is required — that mirrors the API (pan/phoneNumber are optional on
        // /cibil/addDirector and in underwriting's own schema). Guarding on allFieldsValid
        // instead would make Save unreachable on any bureau that doesn't collect PAN: the PAN
        // input is rendered disabled there, so it can never be filled.
        if (!validateInput(director.directorName, 'directorName')) return;
        saveDirector?.(index);
    };

    const handleSendLinkClick = () => {
        sendLink(director.directorName, setDisableSendLink, index, creditBureauConfig?.name, undefined, director.phoneNo, director.pan);
    };
    const isConsentGiven = director?.consentStatus?.[creditBureauConfig?.name]?.isConsentGiven;

    const dataToDisplay: Record<string, { isEditable: boolean }> = creditBureauConfig?.dataToDisplay ?? {};
    // RENDER-ONLY override: for a not-yet-persisted director row, PAN
    // renders editable on every bureau (even Equifax, whose config sets pan.isEditable:false).
    // `allFieldsValid` below keeps reading the RAW `dataToDisplay` — the override never touches
    // the fetch/consent gate, so an already-saved Equifax director's Fetch stays phone-based.
    const effectiveDataToDisplay = getEffectiveDirectorFieldConfig(dataToDisplay, director.isNewDir);
    const allFieldsValid =
        validateInput(director.directorName, 'directorName') &&
        Object.entries(dataToDisplay).every(([field, cfg]) => !cfg.isEditable || validateInput(director[field], field));

    // On the Save step the row only needs a name — /cibil/addDirector takes pan/phoneNumber as
    // optional. Reusing allFieldsValid here would make Save permanently unreachable on a bureau
    // like Equifax, where PAN is configured editable but renders disabled (not required), so it
    // can never be filled and allFieldsValid can never become true for a new director.
    const allFieldsFilled = isSaveStep ? validateInput(director.directorName, 'directorName') : allFieldsValid;

    // Single source of truth for "this action can't be taken", so the icon can never disagree
    // with the greyed-out label. Previously the icon keyed off allFieldsValid alone, which left
    // a purple icon on a dead button whenever consent was the thing blocking the fetch.
    const isActionDisabled = !allFieldsFilled || isFetchBlockedByConsent;

    const renderFetchButton = () => {
        if (isLoading) {
            return <LoadingSpinner color="#6021b3" />;
        }
        return (
            <div className={styles.VerifyImg}>
                <FetchFillIcon colour={isActionDisabled ? '#cbcbcb' : '#6021b3'} height="16" width="16" className={styles.VerifyIcon} />
                <span>{isSaveStep ? 'Save' : isConsentGiven ? 'Refetch' : 'Fetch'}</span>
            </div>
        );
    };

    const renderConsentState = () => {
        const hasBureauError = director?.consentStatus?.[creditBureauConfig?.name]?.isBureauError;
        if (isConsentGiven || hasBureauError) {
            return reFetchSoftPull || hasBureauError ? (
                <div
                    onClick={isSaveStep ? handleSaveClick : isFetchBlockedByConsent ? undefined : handleSendLinkClick}
                    className={clsx(BannerText, {
                        [DisabledBannerText]: !disableSendLink || !allFieldsValid || isFetchBlockedByConsent,
                    })}
                >
                    {renderFetchButton()}
                </div>
            ) : (
                <div className={BannerTextVerified}>
                    <img src={GREEN_BG_WHITE_TICK_CIRCLE} alt="" className={styles.VerifyIcon} />
                    <span>Fetched</span>
                </div>
            );
        }
        return (
            <div
                onClick={isSaveStep ? handleSaveClick : isFetchBlockedByConsent ? undefined : handleSendLinkClick}
                className={clsx(BannerText, {
                    [DisabledBannerText]: !disableSendLink || !allFieldsValid || isFetchBlockedByConsent,
                })}
            >
                {renderFetchButton()}
            </div>
        );
    };

    const isInputDisabled = () => {
        // Inputs are always editable for borrowers — the new bureau-consent flow
        // (RECUR-26209) lets the borrower update PAN / mobile and re-consent at
        // any time. The prior lock keyed off the legacy eSign-style
        // cibilConsentStatus, which doesn't apply here. Editability still respects
        // each field's own `isEditable` config below.
        return false;
    };

    const renderGiveConsentButton = () => {
        if (isLoading) {
            return <LoadingSpinner color="#6021b3" />;
        }
        if (isReceived) {
            return (
                // Reuses the header "Give Consent for All" pill classes (not just visually mirrors
                // them) so this pill is pixel-identical in size/radius/padding to its header and
                // active-state siblings — see .GiveConsentAllReceived in DirectorDetails.module.scss.
                <div className={styles.GiveConsentAllReceived}>
                    <img src={GREEN_CHECK_SMALL} alt="" className={styles.VerifyIcon} />
                    <span>Received</span>
                </div>
            );
        }
        if (disableGiveConsent || !allFieldsValid) {
            const tooltipMessage = disableGiveConsent
                ? 'You are not allowed to complete this action'
                : 'Please enter valid PAN and Mobile Number to proceed';
            return (
                // containerFullWidth: Tooltip's own wrapper is `display: inline-block` with no
                // width of its own, so absent this it shrink-wraps to the pill's text instead of
                // filling the row slot the plain (non-Tooltip) Received / active-Give-Consent
                // pills get for free via their parent's flex stretch.
                <Tooltip
                    fitContent
                    small
                    usePortal
                    containerFullWidth
                    content={<span className={styles.GiveConsentTooltipContent}>{tooltipMessage}</span>}
                    direction="top"
                >
                    {/* No icon here, matching the header's disabled "Give Consent for All" —
                        PURPLE_CHECK_SMALL is a purple checkmark meant for the old light-lavender
                        pill background and would be barely visible against this grey one, but the
                        real reason to drop it is to match the reused .GiveConsentAllCta design. */}
                    <div className={clsx(styles.GiveConsentAllCta, styles.GiveConsentAllCtaDisabled)}>
                        <span>Give Consent</span>
                    </div>
                </Tooltip>
            );
        }
        return (
            // Same size/radius/padding as the header CTA (.GiveConsentAllCta), but the per-row
            // active pill's correct colour is the light-lavender #f9f5ff / #6021b3 — solid purple
            // is only correct for the header "Give Consent for All" (product correction).
            <div onClick={onGiveConsent} className={clsx(styles.GiveConsentAllCta, styles.GiveConsentAllCtaLight)}>
                <span>Give Consent</span>
            </div>
        );
    };

    return (
        <div key={index} className={styles.dflex}>
            <div style={{ width: '30%', minWidth: 0 }} className={styles.FormContainer}>
                <div className={styles.Field}>
                    {director.isNewDir ? (
                        <Input
                            value={director.directorName ?? ''}
                            style={{ margin: 0 }}
                            inputStyle={{ backgroundColor: '#ffffff', borderColor: '#D9D9D9', borderWidth: '0.5px', borderStyle: 'solid' }}
                            onChange={(e) => handleChange(e, index, 'directorName')}
                            placeholder="Director Name"
                        />
                    ) : (
                        <>
                            <div>{director.directorName}</div>
                            {lastConsent ? (
                                <div className={styles.LastConsent}>
                                    <div>Last consent: {dayjs(lastConsent.consentedAt).format('h:mm a, DD MMM YYYY')}</div>
                                    <div>
                                        by {lastConsent.consenterName} ({dayjs(lastConsent.consentedAt).fromNow()})
                                    </div>
                                </div>
                            ) : (
                                <div className={styles.LastConsentEmpty}>Last consent: Not Received</div>
                            )}
                        </>
                    )}
                </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }} className={FormContainer}>
                {/* `dataToDisplay` is per-field config keyed by field name:
                    {"pan": {isEditable: true|false}, "phoneNo": {isEditable: true|false}}.
                    Keys decide which inputs to render (drives iteration).
                    `isEditable` decides whether the input is required/enabled vs read-only/greyed —
                    so all 3 bureaus render the same dual-box layout (Figma contract)
                    while each bureau enforces its own required-field set. */}
                <div className={Field} style={{ display: 'flex', flexDirection: 'row', gap: '8px', position: 'relative' }}>
                    {Object.entries(effectiveDataToDisplay).map(([field, fieldCfg]) => {
                        const isRequired = fieldCfg?.isEditable;
                        // When both PAN and Mobile are FE-invalid, suppress per-field error text so
                        // the row-level "Please enter valid PAN and Mobile number" line below renders
                        // alone (cleaner UX than two separate "Please enter a valid X" lines).
                        const hasBothFieldErrors = !!fieldErrors?.pan && !!fieldErrors?.phoneNo;
                        const fieldValid = isRequired
                            ? !fieldErrors?.[field] && (!isError || !!fieldErrors?.pan || !!fieldErrors?.phoneNo)
                            : true;
                        const inputStyleBase: React.CSSProperties = {
                            backgroundColor: isRequired ? '#ffffff' : '#f9f9f9',
                            borderWidth: '0.5px',
                            borderStyle: 'solid',
                        };
                        if (fieldValid) {
                            inputStyleBase.borderColor = isRequired ? '#D9D9D9' : 'transparent';
                        }
                        return (
                            <Input
                                key={field}
                                name={field}
                                type="text"
                                style={{ margin: 0, flex: 1 }}
                                inputStyle={inputStyleBase}
                                value={director[field] ?? ''}
                                onChange={(e) => isRequired && handleChange(e, index, field)}
                                disabled={!isRequired || isInputDisabled()}
                                placeholder={isRequired ? (field === 'pan' ? 'Director PAN' : 'Director Mobile no.') : ''}
                                errorMessage={isRequired && !hasBothFieldErrors ? fieldErrors?.[field] || '' : ''}
                                isValid={fieldValid}
                            />
                        );
                    })}
                    {/* Row-level error rendering — priority order:
                        1. Both PAN + Mobile FE-invalid  → single combined "Please enter valid PAN and Mobile number"
                        2. BE error AND no per-field error  → the BE error message (e.g. "Consumer Not found in Bureau")
                        3. Only one field invalid           → per-field error renders under that field via Input's errorMessage prop above (this row is hidden)
                        FE field errors always win over BE errors (handleChange clears `isError` on every keystroke, so a stale BE error doesn't reappear once the user starts typing). */}
                    {fieldErrors?.pan && fieldErrors?.phoneNo ? (
                        <div className={styles.RowError}>Please enter valid PAN and Mobile number</div>
                    ) : (
                        isError &&
                        errorMessage &&
                        !fieldErrors?.pan &&
                        !fieldErrors?.phoneNo && <div className={styles.RowError}>{errorMessage}</div>
                    )}
                </div>
            </div>
            <div className={clsx(styles.FetchAction, { [styles.BorrowerConsentAction]: !isAdminSide })}>
                {isAdminSide ? (
                    isFetchBlockedByConsent ? (
                        <Tooltip
                            fitContent
                            small
                            usePortal
                            content={
                                <span className={styles.GiveConsentTooltipContent}>
                                    Bureau consent pending — the borrower must give consent before you can fetch.
                                </span>
                            }
                            direction="top"
                        >
                            {allFieldsFilled ? (
                                renderConsentState()
                            ) : (
                                <div className={styles.FetchButtonDisabled}>{renderFetchButton()}</div>
                            )}
                        </Tooltip>
                    ) : allFieldsFilled ? (
                        renderConsentState()
                    ) : (
                        <div className={styles.FetchButtonDisabled}>{renderFetchButton()}</div>
                    )
                ) : (
                    renderGiveConsentButton()
                )}
                {director.isNewDir && onRemove && (
                    <CrossFilledIcon colour="var(--primary-text-colour)" className={styles.RemoveIcon} onClick={onRemove} />
                )}
            </div>
        </div>
    );
}
export default DirectorInputField;
